import { useState } from 'react';
import { downloadFile, formatProductFilename } from '../utils/downloadUtils';
import { buildApiUrl } from '../utils/apiUtils';
import { analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';

/**
 * Hook for handling file downloads with loading states and analytics
 * @returns {object} Download utilities and state
 */
export const useFileDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  /**
   * Download all files for a product
   * @param {object} product - Product object
   * @param {string} sessionId - Stripe session ID
   */
  const downloadAllFiles = async (product, sessionId) => {
    if (!product || !sessionId) {
      throw new Error('Product and session ID are required');
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      // Log download attempt
      if (analytics) {
        logEvent(analytics, 'download_all_attempt', {
          product_id: product.id,
          product_title: product.title
        });
      }

      // Try the secure download endpoint first if available in the URL
      // This handles cases where the user clicks a link from an email
      let downloadUrl;

      // Check if we're on a page with a secure download URL in the query params
      const urlParams = new URLSearchParams(window.location.search);
      const secureDownloadUrl = urlParams.get('secure_download');

      if (secureDownloadUrl) {
        // Use the secure URL from the query parameter
        downloadUrl = decodeURIComponent(secureDownloadUrl);
      } else {
        // Fall back to the legacy endpoint
        downloadUrl = buildApiUrl(`/api/download-all/${product.id}/${sessionId}`);
      }

      const filename = `${formatProductFilename(product.title)}-all-files.zip`;

      await downloadFile(downloadUrl, filename);

      // Log success
      if (analytics) {
        logEvent(analytics, 'download_all_success', {
          product_id: product.id,
          product_title: product.title
        });
      }

      return { success: true };
    } catch (error) {
      let errorMessage = 'Failed to download files. Please try again.';

      // Check for specific error types
      if (error.message && error.message.includes('403')) {
        // This is likely an expired or invalid download link
        errorMessage = 'Your download link has expired. A new link has been sent to your email.';
      } else if (error.message && error.message.includes('404')) {
        errorMessage = 'The requested files could not be found. Please contact support.';
      }

      setDownloadError(errorMessage);

      // Log error
      if (analytics) {
        logEvent(analytics, 'download_all_error', {
          product_id: product.id,
          product_title: product.title,
          error: error.message
        });
      }

      throw new Error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Clear download error
   */
  const clearDownloadError = () => {
    setDownloadError(null);
  };

  return {
    isDownloading,
    downloadError,
    downloadAllFiles,
    clearDownloadError,
  };
};
